// Project gallery slideshow: the screenshots at the top of a project page.
//
// Advances every slideSpeed ms, sliding the next picture in from the right
// (Previous slides in from the left). Previous/Next buttons and one dot per
// picture are added here, so without JS only the first picture shows. The
// slideshow pauses while the pointer is over it or focus is inside it, and
// doesn't advance on its own for visitors who prefer reduced motion.
const slideTransition = 500;
const slideSpeed = 7000;

function initSlideshow() {
	const gallery = document.querySelector('.images');
	const frame = gallery && gallery.querySelector('.images-scale');
	const links = frame ? [...frame.querySelectorAll('.image-link')] : [];
	if (links.length < 2) {
		return;
	}
	const pictures = links.map(link => link.querySelector('.portfolio-picture'));
	// Read each time: it can change in the accessibility panel while the page is open.
	const reduceMotion = () => document.documentElement.dataset.motion === 'reduce';

	let current = 0;
	let busy = false;
	let hovered = false;
	let focused = false;
	let timer = null;

	// Controls: ‹ dots ›
	const controls = document.createElement('div');
	controls.className = 'slideshow-controls';
	controls.setAttribute('role', 'group');
	controls.setAttribute('aria-label', 'Screenshots');
	const button = (className, label, text) => {
		const b = document.createElement('button');
		b.type = 'button';
		b.className = className;
		b.setAttribute('aria-label', label);
		b.textContent = text;
		return b;
	};
	const prev = button('slideshow-prev', 'Previous screenshot', '‹');
	const next = button('slideshow-next', 'Next screenshot', '›');
	const dotList = document.createElement('div');
	dotList.className = 'slideshow-dots';
	const dots = links.map((_, i) => {
		const dot = button('slideshow-dot', `Screenshot ${i + 1} of ${links.length}`, '');
		dot.addEventListener('click', () => go(i));
		dotList.appendChild(dot);
		return dot;
	});
	controls.append(prev, dotList, next);
	gallery.appendChild(controls);
	gallery.classList.add('has-controls');

	// Place the controls one grid gap below the bottom of the pictures, and make
	// the gallery's margin cover them so the same gap follows before the next
	// section. Measured, because the frame is a fixed 16:10 box that pictures
	// of other shapes don't fill, and on wide screens it's scaled up from its
	// top-right corner. The tallest picture sets the line, so it never jumps.
	const images = pictures.map(p => p.querySelector('img'));
	// Height-to-width ratio from the img's width/height attributes, or its real
	// size once loaded (hidden slides may not have loaded yet). The frame's own
	// 16:10 stands in until at least one size is known.
	const ratio = img => {
		const w = +img.getAttribute('width') || img.naturalWidth;
		const h = +img.getAttribute('height') || img.naturalHeight;
		return w && h ? h / w : 0;
	};
	const page = gallery.closest('main') || document.body;
	const layoutControls = () => {
		const tallest = Math.max(...images.map(ratio)) || 900 / 1440;
		gallery.style.marginBottom = '';
		const gap = parseFloat(getComputedStyle(page).rowGap) || 16;
		const box = gallery.getBoundingClientRect();
		const picture = frame.getBoundingClientRect();
		// The frame crops anything taller than itself (e.g. a portrait report),
		// so the pictures never reach below its bottom edge.
		const visible = Math.min(picture.width * tallest, picture.height);
		const top = picture.top - box.top + visible + gap;
		controls.style.top = `${top}px`;
		controls.style.left = `${picture.left - box.left + picture.width / 2}px`;
		let margin = Math.max(0, top + controls.offsetHeight - box.height);
		gallery.style.marginBottom = `${margin}px`;

		// Whatever comes next may bring its own top margin as well (the case
		// study does on wide screens); give back the extra so it's one gap.
		// offsetTop, not on-screen position: sections may still be flying in.
		const below = gallery.offsetTop + top + controls.offsetHeight;
		const tops = [...page.children]
			.filter(el => el !== gallery && el.offsetParent === gallery.offsetParent)
			.map(el => el.offsetTop)
			.filter(t => t >= below - 1);
		const extra = tops.length ? Math.min(...tops) - below - gap : 0;
		if (extra > .5) {
			margin = Math.max(0, margin - extra);
			gallery.style.marginBottom = `${margin}px`;
		}
	};
	layoutControls();
	images.forEach(img => img.complete || img.addEventListener('load', layoutControls));
	if ('ResizeObserver' in window) {
		new ResizeObserver(layoutControls).observe(gallery);
	} else {
		window.addEventListener('resize', layoutControls);
	}

	// Only the visible picture's link can be focused or clicked.
	const sync = () => {
		links.forEach((link, i) => {
			const shown = i === current;
			link.classList.toggle('active', shown);
			link.inert = !shown;
		});
		dots.forEach((dot, i) => {
			if (i === current) {
				dot.setAttribute('aria-current', 'true');
			} else {
				dot.removeAttribute('aria-current');
			}
		});
	};

	const schedule = () => {
		clearTimeout(timer);
		if (!reduceMotion() && !hovered && !focused) {
			timer = setTimeout(() => go(current + 1), slideSpeed);
		}
	};

	function go(target, direction) {
		target = (target + links.length) % links.length;
		if (busy || target === current) {
			return;
		}
		direction = direction || (target > current || (current === links.length - 1 && target === 0) ? 'next' : 'prev');
		clearTimeout(timer);
		const outgoing = pictures[current];
		const incoming = pictures[target];
		current = target;
		sync();

		if (reduceMotion()) {
			outgoing.classList.remove('active');
			incoming.classList.add('active');
			schedule();
			return;
		}

		busy = true;
		const enterFrom = direction === 'prev' ? 'transition-in-prev' : 'transition-in';
		outgoing.classList.add('transition-out');
		outgoing.classList.toggle('transition-out-prev', direction === 'prev');
		incoming.classList.add(enterFrom);
		// Read layout so the starting position takes effect before it slides in.
		void incoming.offsetWidth;
		outgoing.classList.remove('active');
		incoming.classList.add('active');
		incoming.classList.remove(enterFrom);
		setTimeout(() => {
			outgoing.classList.remove('transition-out', 'transition-out-prev');
			busy = false;
			schedule();
		}, slideTransition);
	}

	prev.addEventListener('click', () => go(current - 1, 'prev'));
	next.addEventListener('click', () => go(current + 1, 'next'));

	gallery.addEventListener('mouseenter', () => { hovered = true; schedule(); });
	gallery.addEventListener('mouseleave', () => { hovered = false; schedule(); });
	gallery.addEventListener('focusin', () => { focused = true; schedule(); });
	gallery.addEventListener('focusout', event => {
		if (!gallery.contains(event.relatedTarget)) {
			focused = false;
			schedule();
		}
	});

	// Stop or resume advancing when motion is switched in the accessibility panel.
	document.addEventListener('a11ychange', schedule);

	sync();
	schedule();
}

// Deferred, so the gallery is already parsed: add the controls right away
// rather than waiting for every image on the page to load.
initSlideshow();
